import type { OrderInfo } from '@@partner:types:app'

import React, { useEffect, useState } from 'react'
import { formatPrice } from '@@partner:helpers/util'

import { Tag, RadioGroup, Radio, Message } from 'rsuite'
import { useFetchCalculateRefund } from '../../../../graphql/query/order/calculateRefund.gql'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'

export interface RefundConfig {
  fullRefund: boolean
  refundMethod: string
  returnToShop: boolean
}

export interface FullRefundContentProps {
  order: OrderInfo
  refundConfig: RefundConfig
  onConfigChange: (newCfg: RefundConfig) => void
}

const FullRefundContent: React.FC<FullRefundContentProps> = ({
  order,
  refundConfig,
  onConfigChange
}) => {
  const [ config, setConfig ] = useState<RefundConfig>({
    ...refundConfig,
    fullRefund: true
  })

  const { data, loading, error } = useFetchCalculateRefund({
    refundInfo: {
      orderId: Number(order.id),
      ...config
    }
  })

  const handleConfigChange = (newState: RefundConfig) => {
    setConfig(newState)
    onConfigChange(newState)
  }

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Не удалось рассчитать возврат"
        error={error}
      />
    )
  }

  const { total = 0, deliveryTotal = 0 } = data?.refundInfo ?? {}
  const sumToRefund = total + deliveryTotal

  return (
    <div className="RefundProducts">
      <div className="my-2">
        {loading && <AppLoader.Inner content="Расчёт суммы возврата" />}

        <label className="font-bold mb-2 block">
          Способ осуществления возврата
        </label>
        <RadioGroup
          onChange={(selectedMethod) => {
            handleConfigChange({
              ...config,
              refundMethod: selectedMethod.toString()
            })
          }}
          value={config.refundMethod}
        >
          <Radio value="money">Деньгами</Radio>
          <Radio value="bonuses">Бонусами на карту</Radio>
        </RadioGroup>
      </div>

      <div className="my-2">
        <span className="font-bold mb-2 block">
          Товар возвращается в магазин?
        </span>
        <RadioGroup
          onChange={(selected) => {
            handleConfigChange({
              ...config,
              returnToShop: selected === 1
            })
          }}
          value={config.returnToShop === true ? 1 : 0}
        >
          <Radio value={1}>Да, товар будет возвращен в магазин</Radio>
          <Radio value={0}>Нет, товар не будет возвращен в магазин</Radio>
        </RadioGroup>
      </div>

      <div className="my-2">
        <span className="font-bold mb-2 block">Полный возврат</span>
      </div>

      <Message
        type="info"
        showIcon
        header={<strong>Информация о возврате</strong>}
      >
        <div>
          <p>Вы собираетесь оформить полный возврат для заказа {order.id}</p>
          <p>
            <strong>Сумма возврата</strong>
          </p>
          <p>
            {' '}
            <Tag color="violet">{formatPrice(sumToRefund)} </Tag>
            <small>
              (товары: {formatPrice(total)}, доставка:{' '}
              {formatPrice(deliveryTotal)})
            </small>
          </p>
        </div>
      </Message>
    </div>
  )
}

export default FullRefundContent
